/**
 * User Activity Service
 * Tracking de atividade de utilizadores via WebSocket (Socket.IO)
 */

import { Injectable, OnDestroy, NgZone } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subject, BehaviorSubject, filter, takeUntil, Observable } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

export interface OnlineUser {
  userId: number | string;
  name: string;
  email?: string;
  userType: string;
  appVersion?: string;
  route: string;
  previousRoute?: string;
  lastActivity: string;
  connectedAt?: string;
  device?: string;
  os?: string;
  browser?: string;
  ip?: string;
  sessionId?: string;
  sessionKey?: string;
  isAnonymous?: boolean;
  sessionDuration?: number;
}

@Injectable({
  providedIn: 'root',
})
export class UserActivityService implements OnDestroy {
  private socket: Socket | null = null;
  private destroy$ = new Subject<void>();
  private connected = false;
  private currentRoute = '/';
  private routeStartTime = Date.now();

  // Observables para eventos
  private onlineUsersSubject = new BehaviorSubject<OnlineUser[]>([]);
  private userJoinSubject = new Subject<any>();
  private userLeaveSubject = new Subject<any>();
  private userNavigateSubject = new Subject<any>();
  private userActionSubject = new Subject<any>();

  public onlineUsers$ = this.onlineUsersSubject.asObservable();
  public userJoin$ = this.userJoinSubject.asObservable();
  public userLeave$ = this.userLeaveSubject.asObservable();
  public userNavigate$ = this.userNavigateSubject.asObservable();
  public userAction$ = this.userActionSubject.asObservable();

  private currentToken: string = '';

  constructor(
    private router: Router,
    private http: HttpClient,
    private ngZone: NgZone,
  ) {
    // Auto-tracking de navegação
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe((event: NavigationEnd) => {
        this.onNavigate(event.urlAfterRedirects);
      });
  }

  /**
   * Obtém ou cria identificador único por separador (preservado em F5 na mesma aba)
   */
  private getOrCreateTabId(): string {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return 'tab_' + Math.random().toString(36).substring(2, 9);
    }
    let tabId = window.sessionStorage.getItem('sv_active_tab_id');
    if (!tabId) {
      tabId = 'tab_' + Date.now().toString(36) + '_' + Math.random().toString(36).substring(2, 7);
      window.sessionStorage.setItem('sv_active_tab_id', tabId);
    }
    return tabId;
  }

  /**
   * Conecta ao Socket.IO server
   */
  connect(token: string): void {
    this.currentToken = token || '';

    if (this.connected || this.socket) {
      this.updateToken(this.currentToken);
      this.requestOnlineUsers();
      return;
    }

    // Garante conexão à API Node.js (porta 8443 em dev ou domínio completo em prod)
    let socketUrl: string;
    const urlConfig = environment.URL;
    if (urlConfig && urlConfig.startsWith('http')) {
      try {
        socketUrl = new URL(urlConfig).origin;
      } catch {
        socketUrl = typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '/';
      }
    } else {
      // Em desenvolvimento, apontar diretamente para a API (8443)
      // para evitar problemas com proxy de WebSockets do Angular CLI
      socketUrl = 'http://localhost:8443';
    }

    let currentRoute = this.router.url || (typeof window !== 'undefined' ? window.location.pathname : '/');
    if (!this.currentToken) {
      const isProtected =
        currentRoute.startsWith('/admin') ||
        currentRoute.startsWith('/clientarea') ||
        currentRoute.startsWith('/intermediaryarea') ||
        currentRoute.startsWith('/crm');
      if (isProtected) {
        currentRoute = '/login';
      }
    }
    const tabId = this.getOrCreateTabId();

    this.socket = io(socketUrl, {
      path: '/socket.io',
      auth: {
        token: this.currentToken,
        appVersion: environment.appVersion,
        currentRoute,
        tabId,
        isActivityTracker: true,
      },
      query: { currentRoute, tabId, isActivityTracker: 'true' },
      transports: ['websocket', 'polling'],
      withCredentials: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });

    this.socket.on('connect', () => {
      this.ngZone.run(() => {
        this.connected = true;
        this.startHeartbeat();
        const activeRoute = this.router.url || (typeof window !== 'undefined' ? window.location.pathname : '/');
        this.onNavigate(activeRoute);
        this.requestOnlineUsers();
      });
    });

    this.socket.on('disconnect', () => {
      this.ngZone.run(() => {
        this.connected = false;
      });
    });

    this.socket.on('connect_error', error => {
      this.ngZone.run(() => {
        console.error('❌ Erro conexão Socket.IO:', error.message);
      });
    });

    // === Eventos para admins (executados na zona do Angular para Change Detection imediata) ===

    this.socket.on('users:list', (users: OnlineUser[]) => {
      this.ngZone.run(() => {
        if (!this.onlineUsersSubject.closed) this.onlineUsersSubject.next(users || []);
      });
    });

    this.socket.on('user:join', data => {
      this.ngZone.run(() => {
        if (!this.userJoinSubject.closed) this.userJoinSubject.next(data);
        this.requestOnlineUsers();
      });
    });

    this.socket.on('user:leave', data => {
      this.ngZone.run(() => {
        if (!this.userLeaveSubject.closed) this.userLeaveSubject.next(data);
        this.requestOnlineUsers();
      });
    });

    this.socket.on('user:navigate', data => {
      this.ngZone.run(() => {
        if (!this.userNavigateSubject.closed) this.userNavigateSubject.next(data);
      });
    });

    this.socket.on('user:action', data => {
      this.ngZone.run(() => {
        if (!this.userActionSubject.closed) this.userActionSubject.next(data);
      });
    });
  }

  private heartbeatInterval: any = null;

  /**
   * Desconecta do Socket.IO
   */
  disconnect(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connected = false;
    }
  }

  /**
   * Heartbeat a cada 20s para manter sessão ativa
   */
  private startHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    this.heartbeatInterval = setInterval(() => {
      if (this.connected && this.socket) {
        this.socket.emit('user:heartbeat');
      }
    }, 20000); // 20s
  }

  /**
   * Tracking de navegação com sanitização para visitantes
   */
  private onNavigate(route: string): void {
    if (!this.connected || !this.socket) return;

    let targetRoute = route;
    if (!this.currentToken) {
      const isProtected =
        targetRoute.startsWith('/admin') ||
        targetRoute.startsWith('/clientarea') ||
        targetRoute.startsWith('/intermediaryarea') ||
        targetRoute.startsWith('/crm');
      if (isProtected) {
        targetRoute = '/login';
      }
    }

    const previousRoute = this.currentRoute;
    const duration = Math.floor((Date.now() - this.routeStartTime) / 1000);

    this.socket.emit('user:navigate', {
      route: targetRoute,
      previousRoute,
      duration,
      appVersion: environment.appVersion,
    });

    this.currentRoute = targetRoute;
    this.routeStartTime = Date.now();
  }

  /**
   * Tracking de ação (click, submit)
   */
  trackAction(type: 'click' | 'submit', target: string): void {
    if (!this.connected || !this.socket) return;

    this.socket.emit('user:action', {
      type,
      target,
      route: this.currentRoute,
    });
  }

  /**
   * Pedir lista atualizada de utilizadores (admin)
   */
  requestOnlineUsers(): void {
    if (this.connected && this.socket) {
      this.socket.emit('users:request');
    }
  }

  /**
   * Obtém a lista e estatísticas de utilizadores online e versões via HTTP (REST)
   */
  getOnlineUsersHttp(): Observable<any> {
    return this.http.get<any>(environment.URL + `/users-monitoring/online`);
  }

  /**
   * Atualiza o token do Socket em tempo real
   * @param token Novo token JWT
   */
  updateToken(token: string): void {
    this.currentToken = token || '';
    if (this.socket) {
      const currentRoute = this.router.url || (typeof window !== 'undefined' ? window.location.pathname : '/');
      const tabId = this.getOrCreateTabId();
      this.socket.auth = {
        token: this.currentToken,
        appVersion: environment.appVersion,
        currentRoute,
        tabId,
        isActivityTracker: true,
      };

      if (this.connected) {
        if (this.currentToken) {
          this.socket.emit('user:auth-update', {
            token: this.currentToken,
            appVersion: environment.appVersion,
            route: currentRoute,
          });
        } else {
          this.socket.emit('user:auth-clear', {
            appVersion: environment.appVersion,
            route: '/login',
          });
        }
      }
    } else if (this.currentToken) {
      this.connect(this.currentToken);
    }
  }

  /**
   * Notifica logout em tempo real
   */
  onLogout(): void {
    this.currentToken = '';
    if (this.socket && this.connected) {
      (this.socket.auth as any).token = '';
      this.socket.emit('user:auth-clear', {
        appVersion: environment.appVersion,
        route: '/home',
      });
      this.onNavigate('/home');
    }
  }

  /**
   * Verifica se está conectado
   */
  isConnected(): boolean {
    return this.connected;
  }

  ngOnDestroy(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    if (!this.destroy$.closed) {
      // Verificação adicionada
      this.destroy$.next();
      this.destroy$.complete();
    }
  }

  /**
   * Obtém os metadados do sistema (MySQL, Servidor, Total Tokens)
   */
  getSystemStatus(): Observable<any> {
    return this.http.get<any>(environment.URL + `/users-monitoring/system-status`);
  }
}
